import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Button from 'components/Button';
import Typography from 'components/Typography';
import Input from 'components/Input';
import classnames from 'classnames/bind';
import { getCookie } from 'utils/cookie';
import { confirmEmail } from 'store/actions/user';
import { MODAL_STATE } from 'store/actions/modal';
import { DASHBOARD_FULL } from 'routes';
import { notifyWithSnack } from 'store/actions/notifications';
import styles from './WelcomeBackModal.module.scss';

const cx = classnames.bind(styles);

const EmailConfirmationForm = ({ onModalClose, onSubmit }) => {
  const methods = useForm({ mode: 'onChange' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { handleSubmit, register } = methods;

  const handleEmailConfirmation = (data) => {
    // If onSubmit prop is provided (for external usage), use it
    if (onSubmit) {
      onSubmit(data);
      return;
    }

    // Default behavior for standalone usage
    const user_key = getCookie('user_token');

    dispatch(confirmEmail(user_key, data))
      .then(() => {
        dispatch(
          notifyWithSnack({
            message: `Email confirmation updated successfully.`,
          }),
        );
        dispatch({ type: MODAL_STATE, status: 'closed' });
        if (onModalClose) onModalClose();
        navigate(DASHBOARD_FULL);
      })
      .catch((error) => {
        const formattedError = error.replace('<![CDATA[', '').replace(']]>', '');
        dispatch(
          notifyWithSnack({
            message: `Error updating email confirmation: ${formattedError}`,
            type: 'error',
          }),
        );
      });
  };

  return (
    <FormProvider {...methods}>
      <div className={cx('modal-header')}>
        <Typography className={cx('title')} type="h2">
          May we keep your email address?
        </Typography>
        <Typography className={cx('libre')} type="body-s">
          In accordance with Mission US's{' '}
          <a href="/data-retention-policy/" target="_blank">
            Data Retention
          </a>{' '}
          and{' '}
          <a href="/privacy-policy/" target="_blank">
            Privacy
          </a>{' '}
          Policies, we require annual confirmation that we may retain your email address in connection with the Mission
          US Dashboard. Please select one of the following:
        </Typography>
      </div>

      <form onSubmit={handleSubmit(handleEmailConfirmation)} className={cx('form-container', 'mgt-20')}>
        <div className={cx('columns')}>
          <div className={cx('email-confirmation-form')}>
            <Input
              type="radio"
              label="Yes, Mission US may retain my email address for another year, and I would like to continue receiving occasional email updates about Mission US."
              name="email_confirmation"
              value="1"
              inputRef={register('email_confirmation')}
              isRequired={false}
              hasMarginBottom={false}
              isRadio={true}
            />
            <Input
              type="radio"
              label="Yes, Mission US may retain my email address for another year for the purposes of the Teacher Dashboard, but I do NOT wish to receive email updates about Mission US."
              name="email_confirmation"
              value="2"
              inputRef={register('email_confirmation')}
              isRequired={false}
              hasMarginBottom={false}
              isRadio={true}
            />
            <Input
              type="radio"
              label="No, I do not consent to Mission US retaining my email address. I understand this means I will be unable to continue using the Teacher Dashboard unless I enter a new email at a later date."
              name="email_confirmation"
              value="3"
              inputRef={register('email_confirmation')}
              isRequired={false}
              hasMarginBottom={true}
              isRadio={true}
            />
          </div>
        </div>

        <div className={cx('modal-header', 'mgb-20')}>
          <Typography className={cx('popup-text')}>
            For further information, read{' '}
            <a href="https://www.mission-us.org/2025/05/19/for-dashboard-users-planning-for-the-end-of-the-school-year-and-the-start-of-a-new-one/">
              this post
            </a>{' '}
            or email us at{' '}
            <a href="mailto:missionus@thirteen.org" target="_blank">
              missionus@thirteen.org
            </a>
            .
          </Typography>
        </div>

        <Button className="btn-submit--absolute mgt-30" label="Confirm" submit />
      </form>
    </FormProvider>
  );
};

export default EmailConfirmationForm;
